import React, { type ReactNode } from 'react';

import { Card, Image } from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, type BoxProps, Stack, Text, xcss } from '@atlaskit/primitives';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

const cardContentStyles = xcss({
	padding: 'space.200',
});

/* For some reason Tag still sets a margin so this negates it. */
const tagContainerStyles = xcss({
	margin: 'space.negative.050',
});

const externalIconStyles = xcss({
	position: 'absolute',
	insetBlockStart: 'space.200',
	insetInlineEnd: 'space.200',
});

const imageContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '160px',
});

type GuidanceCardProps = {
	card: GuidanceCard;
};

/**
 * __Guidance card__
 */
export const GuidanceCard = ({ card }: GuidanceCardProps): React.JSX.Element => {
	return (
		<Card borderColor="color.border" href={card.href}>
			<CardGrid>
				<Box
					backgroundColor={card.backgroundColor || 'elevation.surface.sunken'}
					xcss={imageContainerStyles}
				>
					<Image src={card.image} alt={card.title} />
				</Box>

				<Stack xcss={cardContentStyles} space="space.100" alignBlock="center" alignInline="start">
					<Heading size="small">{card.title}</Heading>
					<Text>{card.description}</Text>
					{card.tag && (
						<Box xcss={tagContainerStyles}>
							<Tag text={card.tag} />
						</Box>
					)}
				</Stack>
				{card.href.startsWith('http') && (
					<Box xcss={externalIconStyles}>
						<LinkExternalIcon label="" color={token('color.icon')} />
					</Box>
				)}
			</CardGrid>
		</Card>
	);
};

export type GuidanceCard = {
	image: any;
	title: string;
	description: string;
	href: string;
	tag?: string;
	backgroundColor?: BoxProps<any>['backgroundColor'];
};

const cardGridStyles = xcss({
	display: 'grid',
	gridTemplateColumns: '1fr',
	'@media (min-width: 30rem)': {
		gridTemplateColumns: '1fr 1fr',
	},
	position: 'relative',
});

const CardGrid = ({ children }: { children: ReactNode }) => (
	<Box xcss={cardGridStyles}>{children}</Box>
);
